import React, { useEffect, useState } from "react";
import { Mutation, Query } from "react-apollo";
import queries from "../../../queries";
import ImageUploader from "../../uploaders/ImageUploader";
import EditorSection from "./EditorSection";
import { parseWheel2Config as parseConfig, Wheel2Disc } from "@treeosk/common";

const setIn = (obj, path, value) => {
  const [head, ...rest] = path;
  return { ...obj, [head]: rest.length ? setIn(obj[head] || {}, rest, value) : value };
};

const Field = ({ label, children, hint }) => (
  <div className="tsk-input-line" style={{ margin: '6px 0' }}>
    <label style={{ display: 'inline-block', minWidth: 230 }}>{label}</label>
    {children}
    {hint && <span style={{ opacity: .6, marginLeft: 8, fontSize: '.9em' }}>{hint}</span>}
  </div>
);

const STAGE_PRESETS = [
  { label: 'Portrait 1080 × 1920', w: 1080, h: 1920 },
  { label: 'Paysage 1920 × 1080', w: 1920, h: 1080 },
  { label: 'Carré 1080 × 1080', w: 1080, h: 1080 },
];

const FIELD_TYPES = [['text', 'Texte'], ['email', 'Email'], ['tel', 'Téléphone'], ['number', 'Nombre'], ['select', 'Liste'], ['checkbox', 'Case à cocher']];

const csvCell = (v) => `"${String(v === undefined || v === null ? '' : v).replace(/"/g, '""')}"`;

const downloadCsv = (plays, config) => {
  const fieldNames = Array.from(new Set([...config.form.fields.map((f) => f.name),
    ...plays.flatMap((p) => Object.keys(p.player_data || {}))]));
  const header = ['date', 'appareil', 'lot', ...fieldNames];
  const rows = plays.map((p) => [
    p.played_at, p.device_id, (config.prizes[p.prize_key] || {}).label || p.prize_key || 'perdu',
    ...fieldNames.map((n) => (p.player_data || {})[n]),
  ]);
  const csv = [header, ...rows].map((r) => r.map(csvCell).join(';')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'roue-parties.csv';
  a.click();
};

const Stats = ({ cellId, config }) => (
  <Query query={queries.cells.wheel2.plays} variables={{ id: cellId, stock_version: config.stockVersion }} fetchPolicy="network-only">
    {({ loading, error, data, refetch }) => {
      if (loading) return <p>Chargement…</p>;
      if (error) return <p>Erreur : {String(error.message)}</p>;
      const plays = data.wheel2Plays || [];
      return (
        <div>
          <p>{plays.length} partie(s) synchronisée(s) · {plays.filter((p) => p.prize_key).length} gagnante(s).
            Les parties des bornes hors-ligne apparaissent ici dès qu'elles retrouvent du réseau.</p>
          <button type="button" className="tsk-button" onClick={() => refetch()}>Actualiser</button>{' '}
          <button type="button" className="tsk-button" disabled={!plays.length} onClick={() => downloadCsv(plays, config)}>Exporter en CSV</button>
        </div>
      );
    }}
  </Query>
);

const Wheel2Form = ({ wheel, save, saving }) => {
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    setDraft({
      title_text: wheel.title_text || '',
      accent_color: wheel.accent_color || '#c20e1a',
      sound_uri: wheel.sound_uri || '',
      config: parseConfig(wheel.config),
    });
  }, [wheel.id, wheel.config, wheel.title_text, wheel.accent_color, wheel.sound_uri]);

  if (!draft) return null;
  const { config } = draft;
  const setTop = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const setCfg = (patch) => setDraft((d) => ({ ...d, config: { ...d.config, ...patch } }));
  const set = (path, value) => setDraft((d) => ({ ...d, config: setIn(d.config, path, value) }));
  const at = (path) => path.reduce((o, k) => (o ? o[k] : undefined), config);

  const Num = ({ path, label, hint, min, max, step }) => (
    <Field label={label} hint={hint}>
      <input type="number" style={{ width: 110 }} min={min} max={max} step={step} value={at(path) === undefined ? '' : at(path)}
        onChange={(e) => set(path, e.target.value === '' ? 0 : Number(e.target.value))} />
    </Field>
  );
  const Check = ({ path, label, hint }) => (
    <Field label={label} hint={hint}><input type="checkbox" checked={!!at(path)} onChange={(e) => set(path, e.target.checked)} /></Field>
  );
  const Txt = ({ path, label, width = 320 }) => (
    <Field label={label}><input type="text" style={{ width }} value={at(path) || ''} onChange={(e) => set(path, e.target.value)} /></Field>
  );

  const prizeKeys = Object.keys(config.prizes);
  const setPrize = (key, patch) => setCfg({ prizes: { ...config.prizes, [key]: { ...config.prizes[key], ...patch } } });
  const addPrize = () => {
    let key = `lot_${prizeKeys.length + 1}`;
    while (config.prizes[key]) key += '_';
    setCfg({ prizes: { ...config.prizes, [key]: { label: 'Nouveau lot', partner: '', stock: 10 } } });
  };
  const removePrize = (key) => {
    const prizes = { ...config.prizes };
    delete prizes[key];
    setCfg({ prizes, segments: config.segments.map((s) => (s === key ? null : s)) });
  };
  const setSegmentCount = (n) => {
    const count = Math.max(0, Math.min(64, n || 0));
    setCfg({ segments: Array.from({ length: count }, (_, i) => config.segments[i] || null) });
  };
  const setSegment = (i, v) => setCfg({ segments: config.segments.map((s, j) => (j === i ? (v || null) : s)) });
  const setSegStyle = (i, patch) => {
    const styles = Array.from({ length: config.segments.length }, (_, j) => (config.segmentStyles || [])[j] || {});
    styles[i] = { ...styles[i], ...patch };
    setCfg({ segmentStyles: styles });
  };
  const autoFill = () => {
    if (!prizeKeys.length) return;
    let c = 0;
    setCfg({ segments: config.segments.map((_, i) => (i % 2 === 0 ? prizeKeys[c++ % prizeKeys.length] : null)) });
  };
  const setPalette = (i, patch) => set(['wheel', 'colors'], config.wheel.colors.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const drawn = config.wheel.source === 'generated';
  const setDay = (i, patch) => setCfg({ days: config.days.map((d, j) => (j === i ? { ...d, ...patch } : d)) });
  const setFormField = (i, patch) => set(['form', 'fields'], config.form.fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));

  const totalStock = prizeKeys.reduce((a, k) => a + (Number(config.prizes[k].stock) || 0), 0);
  const submit = (patch = {}) => save({
    title_text: draft.title_text,
    accent_color: draft.accent_color,
    sound_uri: draft.sound_uri,
    config: JSON.stringify({ ...config, ...patch }),
  });
  const quota = config.mode === 'quota';

  return (
    <div>
      <EditorSection title="Images et son">
        <Field label="Roue"><ImageUploader value={wheel.wheel_uri} canDelete onDelete={() => save({ wheel_uri: '' })} onUpload={(u) => save({ wheel_uri: u })} /></Field>
        <Field label="Flèche"><ImageUploader value={wheel.arrow_uri} canDelete onDelete={() => save({ arrow_uri: '' })} onUpload={(u) => save({ arrow_uri: u })} /></Field>
        <Field label="Fond"><ImageUploader value={wheel.background_uri} canDelete onDelete={() => save({ background_uri: '' })} onUpload={(u) => save({ background_uri: u })} /></Field>
        <Field label="Son (URL mp3)" hint="téléchargé dans l'export, donc utilisable hors-ligne">
          <input type="text" style={{ width: 360 }} value={draft.sound_uri} onChange={(e) => setTop({ sound_uri: e.target.value })} />
        </Field>
      </EditorSection>

      <EditorSection title="Général et tirage">
        <Field label="Titre"><input type="text" value={draft.title_text} onChange={(e) => setTop({ title_text: e.target.value })} /></Field>
        <Field label="Couleur principale"><input type="color" value={draft.accent_color} onChange={(e) => setTop({ accent_color: e.target.value })} /></Field>
        <Field label="Code pour lancer" hint="vide = pas de code, la roue se lance d'un bouton">
          <input type="text" value={config.code} onChange={(e) => set(['code'], e.target.value.replace(/\D/g, ''))} />
        </Field>
        {Check({ path: ['paused'], label: "Roue en pause" })}
        {Check({ path: ['testMode'], label: "Mode essai", hint: "horaires ignorés, compteurs séparés, stock réel intact" })}
        <Field label="Mode de tirage">
          <select value={config.mode} onChange={(e) => set(['mode'], e.target.value)}>
            <option value="quota">Quotas par jour et par heure (étale les lots sur l'événement)</option>
            <option value="random">Hasard (X % de parties gagnantes)</option>
          </select>
        </Field>
        {!quota && Num({ path: ['winRate'], label: "Parties gagnantes (%)", min: 0, max: 100 })}
        {!quota && Check({ path: ['useSchedule'], label: "Respecter les jours et horaires" })}
        {Check({ path: ['shareStock'], label: "Partager le stock entre les bornes", hint: "chaque borne déduit ce que les autres ont donné, dès qu'elle a du réseau" })}
      </EditorSection>

      <EditorSection title={`Lots et stocks (${totalStock} au total)`}>
        <Query query={queries.cells.wheel2.stats} variables={{ id: wheel.id, stock_version: config.stockVersion }} fetchPolicy="network-only">
          {({ data }) => {
            const given = {};
            ((data && data.wheel2Stats) || []).forEach((c) => { given[c.prize_key] = c.count; });
            return (
              <>
                <p style={{ opacity: .7 }}>
                  « Donnés » vient des bornes synchronisées (une borne hors-ligne n'apparaît qu'à son retour sur le réseau).
                  Le stock total s'applique dès que la borne recharge le projet.
                </p>
                {prizeKeys.map((k) => {
                  const p = config.prizes[k];
                  const g = given[k] || 0;
                  return (
                    <div key={k} style={{ border: '1px solid #8884', borderRadius: 8, padding: 10, margin: '8px 0' }}>
                      <Field label="Nom du lot"><input type="text" style={{ width: 320 }} value={p.label || ''} onChange={(e) => setPrize(k, { label: e.target.value })} /></Field>
                      <Field label="Partenaire"><input type="text" style={{ width: 320 }} value={p.partner || ''} onChange={(e) => setPrize(k, { partner: e.target.value })} /></Field>
                      <Field label="Stock total" hint={`${g} donné(s) · ${Math.max((Number(p.stock) || 0) - g, 0)} restant(s)`}>
                        <input type="number" style={{ width: 110 }} min={0} value={p.stock} onChange={(e) => setPrize(k, { stock: Math.max(0, Number(e.target.value)) })} />
                      </Field>
                      <Field label="Poids" hint="vide = proportionnel au stock restant">
                        <input type="number" style={{ width: 110 }} min={0} value={p.weight || ''} onChange={(e) => setPrize(k, { weight: Number(e.target.value) || 0 })} />
                      </Field>
                      <Field label="Visuel du résultat" hint="sinon : nom + partenaire">
                        <ImageUploader value={p.image_uri} canDelete onDelete={() => setPrize(k, { image_uri: '' })} onUpload={(u) => setPrize(k, { image_uri: u })} />
                      </Field>
                      <button type="button" className="tsk-button" onClick={() => removePrize(k)}>Supprimer ce lot</button>
                    </div>
                  );
                })}
              </>
            );
          }}
        </Query>
        <button type="button" className="tsk-button" onClick={addPrize}>Ajouter un lot</button>
        <div style={{ marginTop: 16 }}>
          <button type="button" className="tsk-button" onClick={() => {
            if (window.confirm('Remettre à zéro les compteurs de TOUTES les bornes (nouveau stock complet) ?')) submit({ stockVersion: (config.stockVersion || 1) + 1 });
          }}>
            Remettre à zéro les compteurs des bornes
          </button>
          <span style={{ opacity: .7, marginLeft: 8 }}>(version {config.stockVersion})</span>
        </div>
      </EditorSection>

      <EditorSection title="Design de la roue">
        <Field label="Type de roue">
          <select value={config.wheel.source} onChange={(e) => set(['wheel', 'source'], e.target.value)}>
            <option value="image">Image importée (section Images)</option>
            <option value="generated">Roue dessinée (segments, couleurs, textes et images de lots)</option>
          </select>
        </Field>
        {drawn && (
          <>
            <div style={{ maxWidth: 300, margin: '10px 0' }}>
              <Wheel2Disc config={config} />
            </div>
            <h4>Couleurs des segments (répétées en boucle)</h4>
            {config.wheel.colors.map((c, i) => (
              <Field key={i} label={`Couleur ${i + 1}`}>
                fond <input type="color" value={c.bg} onChange={(e) => setPalette(i, { bg: e.target.value })} />{' '}
                texte <input type="color" value={c.text} onChange={(e) => setPalette(i, { text: e.target.value })} />{' '}
                {config.wheel.colors.length > 1 && <button type="button" className="tsk-button" onClick={() => set(['wheel', 'colors'], config.wheel.colors.filter((_, j) => j !== i))}>✕</button>}
              </Field>
            ))}
            <button type="button" className="tsk-button" onClick={() => set(['wheel', 'colors'], [...config.wheel.colors, { bg: '#f5b700', text: '#000000' }])}>Ajouter une couleur</button>
            <h4>Textes</h4>
            {Check({ path: ['wheel', 'showLabels'], label: 'Afficher les textes sur la roue' })}
            {Txt({ path: ['wheel', 'loseLabel'], label: 'Texte des segments perdus' })}
            {Txt({ path: ['wheel', 'fontFamily'], label: 'Police', width: 220 })}
            {Num({ path: ['wheel', 'fontSize'], label: 'Taille max du texte', min: 14, max: 140 })}
            {Num({ path: ['wheel', 'textRadius'], label: 'Position du texte (% du rayon)', min: 20, max: 95 })}
            <Field label="Sens du texte">
              <select value={config.wheel.textDirection} onChange={(e) => set(['wheel', 'textDirection'], e.target.value)}>
                <option value="outward">Du centre vers l'extérieur</option>
                <option value="inward">De l'extérieur vers le centre</option>
              </select>
            </Field>
            <Field label="Graisse">
              <select value={config.wheel.fontWeight} onChange={(e) => set(['wheel', 'fontWeight'], e.target.value)}>
                <option value="normal">Normale</option>
                <option value="bold">Grasse</option>
              </select>
            </Field>
            <h4>Images des lots</h4>
            {Num({ path: ['wheel', 'imageSize'], label: 'Taille des images', min: 20, max: 400 })}
            {Num({ path: ['wheel', 'imageRadius'], label: 'Position (% du rayon)', min: 20, max: 95 })}
            <h4>Contour, séparateurs et centre</h4>
            {Num({ path: ['wheel', 'borderWidth'], label: 'Épaisseur du contour', min: 0, max: 80 })}
            <Field label="Couleur du contour"><input type="color" value={config.wheel.borderColor} onChange={(e) => set(['wheel', 'borderColor'], e.target.value)} /></Field>
            {Num({ path: ['wheel', 'separatorWidth'], label: 'Épaisseur des séparateurs', min: 0, max: 20 })}
            <Field label="Couleur des séparateurs"><input type="color" value={config.wheel.separatorColor} onChange={(e) => set(['wheel', 'separatorColor'], e.target.value)} /></Field>
            {Check({ path: ['wheel', 'hub', 'show'], label: 'Pastille au centre' })}
            {config.wheel.hub.show && (
              <>
                {Num({ path: ['wheel', 'hub', 'size'], label: 'Taille de la pastille', min: 20, max: 400 })}
                <Field label="Couleur de la pastille"><input type="color" value={config.wheel.hub.color} onChange={(e) => set(['wheel', 'hub', 'color'], e.target.value)} /></Field>
                <Field label="Logo au centre"><ImageUploader value={config.wheel.hub.image_uri} canDelete onDelete={() => set(['wheel', 'hub', 'image_uri'], '')} onUpload={(u) => set(['wheel', 'hub', 'image_uri'], u)} /></Field>
              </>
            )}
          </>
        )}
      </EditorSection>

      <EditorSection title="Segments de la roue">
        <p style={{ opacity: .7 }}>Dans l'ordre, en partant du haut dans le sens des aiguilles d'une montre. Le lot choisi détermine ce que gagne le joueur quand la roue s'arrête sur ce segment.</p>
        <Field label="Nombre de segments"><input type="number" style={{ width: 110 }} min={1} max={64} value={config.segments.length} onChange={(e) => setSegmentCount(Number(e.target.value))} /></Field>
        {Num({ path: ['segmentOffset'], label: 'Décalage (degrés)', hint: "si le 1er segment n'est pas centré sous la flèche", min: -360, max: 360 })}
        <button type="button" className="tsk-button" onClick={autoFill}>Remplir automatiquement (un lot, un perdu, en alternance)</button>
        {config.segments.map((s, i) => {
          const st = (config.segmentStyles || [])[i] || {};
          return (
            <div key={i} style={{ borderBottom: '1px solid #8883', padding: '6px 0' }}>
              <Field label={`Segment ${i + 1}`}>
                <select value={s || ''} onChange={(e) => setSegment(i, e.target.value)}>
                  <option value="">Perdu</option>
                  {prizeKeys.map((k) => <option key={k} value={k}>{config.prizes[k].label || k}</option>)}
                </select>
              </Field>
              {drawn && (
                <div style={{ marginLeft: 20 }}>
                  <Field label="Texte" hint="« | » pour passer à la ligne ; vide = nom du lot">
                    <input type="text" style={{ width: 240 }} value={st.label || ''} onChange={(e) => setSegStyle(i, { label: e.target.value })} />
                  </Field>
                  <Field label="Couleurs">
                    fond <input type="color" value={st.bg || config.wheel.colors[i % config.wheel.colors.length].bg} onChange={(e) => setSegStyle(i, { bg: e.target.value })} />{' '}
                    texte <input type="color" value={st.text || config.wheel.colors[i % config.wheel.colors.length].text} onChange={(e) => setSegStyle(i, { text: e.target.value })} />{' '}
                    <button type="button" className="tsk-button" onClick={() => setSegStyle(i, { bg: '', text: '' })}>Auto</button>
                  </Field>
                  <Field label="Image sur le segment">
                    <ImageUploader value={st.image_uri} canDelete onDelete={() => setSegStyle(i, { image_uri: '' })} onUpload={(u) => setSegStyle(i, { image_uri: u })} />
                  </Field>
                </div>
              )}
            </div>
          );
        })}
      </EditorSection>

      <EditorSection title="Planning" defaultOpen={false}>
        <p style={{ opacity: .7 }}>Jours et horaires d'ouverture. En mode quotas, le stock restant est réparti sur les jours restants puis sur les heures.</p>
        {config.days.map((d, i) => (
          <Field key={i} label={`Jour ${i + 1}`}>
            <input type="date" value={d.date} onChange={(e) => setDay(i, { date: e.target.value })} />{' '}
            <input type="time" value={d.open} onChange={(e) => setDay(i, { open: e.target.value })} /> →{' '}
            <input type="time" value={d.close} onChange={(e) => setDay(i, { close: e.target.value })} />{' '}
            <button type="button" className="tsk-button" onClick={() => setCfg({ days: config.days.filter((_, j) => j !== i) })}>✕</button>
          </Field>
        ))}
        <button type="button" className="tsk-button" onClick={() => setCfg({ days: [...config.days, { date: '', open: '10:00', close: '18:00' }] })}>Ajouter un jour</button>
        {quota && (
          <>
            {Num({ path: ['peak', 'start'], label: "Heures de pointe : de (h)", min: 0, max: 23 })}
            {Num({ path: ['peak', 'end'], label: "à (h)", min: 1, max: 24 })}
            {Num({ path: ['peak', 'weight'], label: "Poids de la pointe", hint: "1 = pas de pointe", min: 1, step: 0.5 })}
            {Num({ path: ['rate'], label: "Parties/minute minimum estimées", min: 0.1, step: 0.1 })}
          </>
        )}
      </EditorSection>

      <EditorSection title="Options" defaultOpen={false}>
        {Check({ path: ['options', 'idleSpin'], label: "La roue tourne en attendant un joueur" })}
        {config.options.idleSpin && Num({ path: ['options', 'idleSpinSeconds'], label: "Secondes par tour (attente)", min: 2, max: 120 })}
        {Check({ path: ['options', 'countdown'], label: "Décompte 3-2-1-GO" })}
        {Check({ path: ['options', 'sound'], label: "Son pendant le tirage" })}
        {Num({ path: ['options', 'spinSeconds'], label: "Durée du tour (s)", min: 3, max: 60 })}
        {Check({ path: ['options', 'showResult'], label: "Écran de résultat" })}
        {config.options.showResult && Num({ path: ['options', 'resultSeconds'], label: "Durée du résultat (s)", min: 2, max: 120 })}
        <Field label="Fond des panneaux"><input type="color" value={config.options.panelBackground} onChange={(e) => set(['options', 'panelBackground'], e.target.value)} /></Field>
      </EditorSection>

      <EditorSection title="Formulaire joueur" defaultOpen={false}>
        {Check({ path: ['form', 'enabled'], label: "Demander des informations avant de jouer" })}
        {config.form.enabled && (
          <>
            {Txt({ path: ['form', 'title'], label: "Titre du formulaire" })}
            {config.form.fields.map((f, i) => (
              <div key={i} style={{ border: '1px solid #8884', borderRadius: 8, padding: 10, margin: '8px 0' }}>
                <Field label="Libellé"><input type="text" value={f.label || ''} onChange={(e) => setFormField(i, { label: e.target.value, name: f.name || (e.target.value || 'champ').toLowerCase().replace(/[^a-z0-9]+/g, '_') })} /></Field>
                <Field label="Type">
                  <select value={f.type || 'text'} onChange={(e) => setFormField(i, { type: e.target.value })}>
                    {FIELD_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </Field>
                {f.type === 'select' && (
                  <Field label="Choix" hint="séparés par une virgule">
                    <input type="text" style={{ width: 320 }} value={(f.options || []).join(', ')} onChange={(e) => setFormField(i, { options: e.target.value.split(',').map((o) => o.trim()).filter(Boolean) })} />
                  </Field>
                )}
                <Field label="Obligatoire"><input type="checkbox" checked={!!f.required} onChange={(e) => setFormField(i, { required: e.target.checked })} /></Field>
                <button type="button" className="tsk-button" onClick={() => set(['form', 'fields'], config.form.fields.filter((_, j) => j !== i))}>Supprimer ce champ</button>
              </div>
            ))}
            <button type="button" className="tsk-button" onClick={() => set(['form', 'fields'], [...config.form.fields, { name: `champ_${config.form.fields.length + 1}`, label: 'Nouveau champ', type: 'text', required: true }])}>Ajouter un champ</button>
          </>
        )}
      </EditorSection>

      <EditorSection title="Mise en page" defaultOpen={false}>
        <p style={{ opacity: .7 }}>Positions en pixels dans la scène (le coin haut-gauche est 0, 0). La scène est mise à l'échelle pour remplir la cellule.</p>
        <Field label="Format de la scène">
          <select value={`${config.stage.width}x${config.stage.height}`} onChange={(e) => { const [w, h] = e.target.value.split('x').map(Number); set(['stage'], { width: w, height: h }); }}>
            {STAGE_PRESETS.map((p) => <option key={p.label} value={`${p.w}x${p.h}`}>{p.label}</option>)}
            {!STAGE_PRESETS.some((p) => p.w === config.stage.width && p.h === config.stage.height) && <option value={`${config.stage.width}x${config.stage.height}`}>{config.stage.width} × {config.stage.height}</option>}
          </select>
        </Field>
        {Num({ path: ['stage', 'width'], label: "Largeur de la scène", min: 200, max: 4000 })}
        {Num({ path: ['stage', 'height'], label: "Hauteur de la scène", min: 200, max: 4000 })}
        <h4>Roue</h4>
        {Num({ path: ['layout', 'wheel', 'x'], label: "X" })}{Num({ path: ['layout', 'wheel', 'y'], label: "Y" })}{Num({ path: ['layout', 'wheel', 'size'], label: "Taille (largeur)", min: 50 })}
        <h4>Flèche</h4>
        {Check({ path: ['layout', 'arrow', 'show'], label: "Afficher" })}
        {Num({ path: ['layout', 'arrow', 'x'], label: "X" })}{Num({ path: ['layout', 'arrow', 'y'], label: "Y" })}{Num({ path: ['layout', 'arrow', 'size'], label: "Taille (largeur)", min: 10 })}
        <h4>Titre</h4>
        {Check({ path: ['layout', 'title', 'show'], label: "Afficher" })}
        {Num({ path: ['layout', 'title', 'x'], label: "X" })}{Num({ path: ['layout', 'title', 'y'], label: "Y" })}
        {Num({ path: ['layout', 'title', 'w'], label: "Largeur" })}{Num({ path: ['layout', 'title', 'h'], label: "Hauteur" })}{Num({ path: ['layout', 'title', 'size'], label: "Taille du texte" })}
        <Field label="Couleur du texte"><input type="color" value={config.layout.title.color} onChange={(e) => set(['layout', 'title', 'color'], e.target.value)} /></Field>
        <h4>Panneau code / formulaire</h4>
        {Num({ path: ['layout', 'form', 'x'], label: "X" })}{Num({ path: ['layout', 'form', 'y'], label: "Y" })}{Num({ path: ['layout', 'form', 'w'], label: "Largeur", min: 200 })}
        <h4>Décompte</h4>
        {Num({ path: ['layout', 'timer', 'y'], label: "Y" })}{Num({ path: ['layout', 'timer', 'size'], label: "Taille du chiffre", min: 40 })}
        <h4>Résultat</h4>
        {Num({ path: ['layout', 'result', 'x'], label: "X" })}{Num({ path: ['layout', 'result', 'y'], label: "Y" })}{Num({ path: ['layout', 'result', 'w'], label: "Largeur", min: 200 })}
      </EditorSection>

      <EditorSection title="Textes" defaultOpen={false}>
        {[['launch', 'Titre du panneau'], ['confirm', 'Bouton avec code'], ['play', 'Bouton sans code'], ['codeLabel', 'Placeholder du code'], ['wrongCode', 'Code incorrect'],
          ['needCode', 'Code manquant'], ['missingFields', 'Champs manquants'], ['paused', 'Roue en pause'], ['notConfigured', 'Non configurée'],
          ['win', 'Gagné : titre'], ['lose', 'Perdu : titre'], ['loseSub', 'Perdu : sous-titre'], ['thanks', 'Perdu : remerciement']].map(([k, l]) => (
          <React.Fragment key={k}>{Txt({ path: ['texts', k], label: l })}</React.Fragment>
        ))}
      </EditorSection>

      <EditorSection title="Parties et joueurs" defaultOpen={false}>
        <Stats cellId={wheel.id} config={config} />
      </EditorSection>

      <br />
      <button type="button" className="tsk-button" disabled={saving} onClick={() => submit()}>Enregistrer</button>
    </div>
  );
};

const Wheel2CellEditor = ({ cell }) => {
  if (!cell) return null;
  return (
    <Query query={queries.cells.wheel2.get} variables={{ id: cell.id }}>
      {({ loading, error, data }) => {
        if (loading) return null;
        if (error) return `Error!: ${error}`;
        const wheel = data.wheel2Cell;
        if (!wheel) return null;
        return (
          <Mutation
            mutation={queries.cells.wheel2.update}
            refetchQueries={() => [{ query: queries.cells.wheel2.get, variables: { id: cell.id } }]}
          >
            {(mutate, { loading: saving }) => (
              <Wheel2Form wheel={wheel} saving={saving} save={(patch) => mutate({ variables: { input: { id: cell.id, ...patch } } })} />
            )}
          </Mutation>
        );
      }}
    </Query>
  );
};

export default Wheel2CellEditor;
